/**
 * Global HR — Sprint 4 · Training · Policies · Offboarding.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Pencil, X, Book, ClipboardList, LogOut, Users, CheckCircle2, Download, FileText, GraduationCap } from 'lucide-react';
import { apiClient, API } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const Card = ({ children, className = '' }) => (<div className={`bg-white border border-black/5 rounded-2xl ${className}`}>{children}</div>);
const H1 = ({ eyebrow, title, sub }) => (
  <div className="mb-6">
    <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">{eyebrow}</span>
    <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{title}</h1>
    {sub && <p className="mt-1 text-sm text-[#0b1a2e]/60">{sub}</p>}
  </div>
);
const Btn = ({ variant = 'primary', className = '', ...rest }) => {
  const styles = { primary: 'bg-[#0f5c3d] text-white hover:bg-[#0b4a30]', ghost: 'border border-[#0f5c3d]/20 text-[#0f5c3d] hover:bg-[#f7fbf8]' }[variant];
  return <button {...rest} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${styles} ${className}`} />;
};
const Input = (p) => <input {...p} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm ${p.className || ''}`} />;
const Select = (p) => <select {...p} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white ${p.className || ''}`}>{p.children}</select>;
const Field = ({ label, children }) => (<div><label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">{label}</label>{children}</div>);
const Badge = ({ color, children }) => <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${color}`}>{children}</span>;

// ============================================================
// TRAINING
// ============================================================
export const TrainingPage = () => {
  const [tab, setTab] = useState('catalog');
  return (
    <div>
      <H1 eyebrow="Global HR · Sprint 4" title="Training" sub="Course catalogue, enrolments and printable completion certificates." />
      <div className="flex gap-2 mb-4">
        {[['catalog', 'Catalogue', Book], ['enrolments', 'Enrolments', GraduationCap]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`train-tab-${k}`} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold ${tab === k ? 'bg-[#0f5c3d] text-white' : 'border border-[#0f5c3d]/20 text-[#0f5c3d]'}`}><Icon className="w-3.5 h-3.5" /> {l}</button>
        ))}
      </div>
      {tab === 'catalog' && <CoursesTab />}
      {tab === 'enrolments' && <EnrolmentsTab />}
    </div>
  );
};

const CoursesTab = () => {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [enrolling, setEnrolling] = useState(null);
  const load = useCallback(async () => {
    const [c, u] = await Promise.all([apiClient.get('/hr/training/courses'), apiClient.get('/users/directory')]);
    setItems(c.data.data || []); setUsers(u.data.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (c) => { if (window.confirm(`Delete ${c.title}?`)) { await apiClient.delete(`/hr/training/courses/${c.id}`); load(); } };
  return (
    <>
      <div className="flex justify-end mb-4"><Btn onClick={() => setEditing({})} data-testid="new-course-btn"><Plus className="w-3.5 h-3.5" /> New course</Btn></div>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr><th className="text-left p-2">Code</th><th className="text-left p-2">Title</th><th className="text-left p-2">Mode</th><th className="text-right p-2">Hours</th><th className="text-left p-2">Mandatory</th><th></th></tr></thead>
          <tbody>{items.map((c) => (
            <tr key={c.id} className="border-t border-black/5" data-testid={`course-row-${c.code}`}>
              <td className="p-2 font-mono text-xs">{c.code}</td>
              <td className="p-2"><Book className="w-3.5 h-3.5 inline text-[#0f5c3d] mr-1.5" />{c.title}</td>
              <td className="p-2 text-xs">{c.mode}</td>
              <td className="p-2 text-right text-xs">{c.duration_hours || '—'}</td>
              <td className="p-2">{c.mandatory && <Badge color="bg-amber-100 text-amber-800">Mandatory</Badge>}</td>
              <td className="p-2 text-right whitespace-nowrap">
                <Btn variant="ghost" onClick={() => setEnrolling(c)} data-testid={`enroll-${c.code}`}><Users className="w-3.5 h-3.5" /> Enroll</Btn>
                <button onClick={() => setEditing(c)} className="text-[#0f5c3d] ml-2"><Pencil className="w-3.5 h-3.5 inline" /></button>
                <button onClick={() => del(c)} className="text-red-600 ml-1"><Trash2 className="w-3.5 h-3.5 inline" /></button>
              </td>
            </tr>
          ))}</tbody>
        </table>
        {items.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No courses yet.</div>}
      </Card>
      {editing && <CourseModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {enrolling && <EnrollModal course={enrolling} users={users} onClose={() => setEnrolling(null)} onSaved={() => setEnrolling(null)} />}
    </>
  );
};

const CourseModal = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState({ code: '', title: '', category: '', description: '', duration_hours: '', mode: 'self_paced', validity_months: '', mandatory: false, active: true, ...initial });
  const save = async () => {
    try {
      const p = { ...f, duration_hours: f.duration_hours ? parseFloat(f.duration_hours) : null, validity_months: f.validity_months ? parseInt(f.validity_months, 10) : null, code: (f.code || '').toUpperCase() };
      if (f.id) await apiClient.patch(`/hr/training/courses/${f.id}`, p); else await apiClient.post('/hr/training/courses', p);
      toast({ title: 'Saved' }); onSaved();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">{f.id ? 'Edit' : 'New'} course</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Code"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="cc-code" /></Field>
          <Field label="Category"><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="cc-title" /></Field></div>
          <Field label="Mode"><Select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}>{['self_paced', 'instructor_led', 'external', 'blended'].map((m) => <option key={m} value={m}>{m}</option>)}</Select></Field>
          <Field label="Hours"><Input type="number" value={f.duration_hours} onChange={(e) => setF({ ...f, duration_hours: e.target.value })} /></Field>
          <Field label="Validity (months)"><Input type="number" value={f.validity_months} onChange={(e) => setF({ ...f, validity_months: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.mandatory} onChange={(e) => setF({ ...f, mandatory: e.target.checked })} /> Mandatory</label>
          <div className="col-span-2 flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="cc-save">Save</Btn></div>
        </div>
      </div>
    </div>
  );
};

const EnrollModal = ({ course, users, onClose, onSaved }) => {
  const [selected, setSelected] = useState({});
  const [dueDate, setDueDate] = useState('');
  const submit = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return toast({ title: 'Select at least one', variant: 'destructive' });
    try { const r = await apiClient.post('/hr/training/enroll', { course_id: course.id, user_ids: ids, due_date: dueDate || null }); toast({ title: `${r.data.inserted} enrolled` }); onSaved(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">Enroll in {course.title}</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <Field label="Due date (optional)"><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></Field>
          <div className="max-h-[40vh] overflow-y-auto border border-black/5 rounded">
            {users.map((u) => (
              <label key={u.user_id} className="flex items-center gap-2 p-2 border-b border-black/5 hover:bg-[#f7fbf8] cursor-pointer">
                <input type="checkbox" checked={!!selected[u.user_id]} onChange={(e) => setSelected({ ...selected, [u.user_id]: e.target.checked })} data-testid={`enr-${u.user_id}`} />
                <span className="text-sm">{u.name || u.email}</span>
                <span className="text-xs text-[#0b1a2e]/50 ml-auto">{u.role}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="p-4 border-t bg-[#f7fbf8] flex justify-end gap-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={submit} data-testid="enr-submit">Enroll</Btn></div>
      </div>
    </div>
  );
};

const EnrolmentsTab = () => {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const load = useCallback(async () => {
    const [e, u] = await Promise.all([apiClient.get('/hr/training/enrolments'), apiClient.get('/users/directory')]);
    setItems(e.data.data || []); setUsers(u.data.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const complete = async (e) => {
    const s = window.prompt('Score (optional):', '');
    try { await apiClient.post(`/hr/training/enrolments/${e.id}/complete`, { score: s ? parseFloat(s) : null }); toast({ title: 'Marked complete' }); load(); }
    catch (err) { toast({ title: err?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <Card>
      <table className="w-full text-sm">
        <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr><th className="text-left p-2">Course</th><th className="text-left p-2">Employee</th><th className="text-left p-2">Due</th><th className="text-left p-2">Status</th><th className="text-right p-2">Score</th><th></th></tr></thead>
        <tbody>{items.map((e) => {
          const u = users.find((uu) => uu.user_id === e.user_id);
          return (
            <tr key={e.id} className="border-t border-black/5" data-testid={`enrol-row-${e.id}`}>
              <td className="p-2">{e.course_title}</td>
              <td className="p-2">{u?.name || e.user_id}</td>
              <td className="p-2 text-xs">{e.due_date || '—'}</td>
              <td className="p-2"><Badge color={e.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>{e.status}</Badge></td>
              <td className="p-2 text-right text-xs">{e.score || '—'}</td>
              <td className="p-2 text-right whitespace-nowrap">
                {e.status !== 'completed' && <Btn variant="ghost" onClick={() => complete(e)} data-testid={`complete-${e.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Mark done</Btn>}
                {e.status === 'completed' && <a href={`${API}/api/hr/training/enrolments/${e.id}/certificate.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0f5c3d] text-xs"><Download className="w-3.5 h-3.5" /> Certificate</a>}
              </td>
            </tr>
          );
        })}</tbody>
      </table>
      {items.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No enrolments yet.</div>}
    </Card>
  );
};

// ============================================================
// POLICIES
// ============================================================
export const PoliciesPage = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const load = useCallback(async () => { const r = await apiClient.get('/hr/policies'); setItems(r.data.data || []); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (p) => { if (window.confirm(`Delete ${p.title}?`)) { await apiClient.delete(`/hr/policies/${p.id}`); load(); } };
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · Sprint 4" title="Policy library" sub="Central policy repository. Employees acknowledge each version; HR sees who signed off." />
        <div className="flex gap-2 mb-6"><Btn onClick={() => setEditing({})} data-testid="new-policy-btn"><Plus className="w-3.5 h-3.5" /> New policy</Btn></div>
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr><th className="text-left p-2">Code</th><th className="text-left p-2">Title</th><th className="text-left p-2">Version</th><th className="text-left p-2">Category</th><th className="text-left p-2">Effective</th><th className="text-left p-2">Ack required</th><th></th></tr></thead>
          <tbody>{items.map((p) => (
            <tr key={p.id} className="border-t border-black/5" data-testid={`policy-row-${p.code}`}>
              <td className="p-2 font-mono text-xs">{p.code}</td>
              <td className="p-2"><ClipboardList className="w-3.5 h-3.5 inline text-[#0f5c3d] mr-1.5" />{p.title}</td>
              <td className="p-2 text-xs">v{p.version}</td>
              <td className="p-2 text-xs">{p.category || '—'}</td>
              <td className="p-2 text-xs">{p.effective_from || '—'}</td>
              <td className="p-2">{p.requires_ack && <Badge color="bg-amber-100 text-amber-800">Yes</Badge>}</td>
              <td className="p-2 text-right whitespace-nowrap">
                <Btn variant="ghost" onClick={() => setViewing(p)} data-testid={`view-policy-${p.code}`}>View</Btn>
                <button onClick={() => setEditing(p)} className="text-[#0f5c3d] ml-2"><Pencil className="w-3.5 h-3.5 inline" /></button>
                <button onClick={() => del(p)} className="text-red-600 ml-1"><Trash2 className="w-3.5 h-3.5 inline" /></button>
              </td>
            </tr>
          ))}</tbody>
        </table>
        {items.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No policies yet.</div>}
      </Card>
      {editing && <PolicyModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {viewing && <PolicyViewModal policy={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
};

const PolicyModal = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState({ code: '', title: '', version: '1.0', category: '', body: '', effective_from: '', requires_ack: true, active: true, ...initial });
  const save = async () => {
    try { const p = { ...f, effective_from: f.effective_from || null, code: (f.code || '').toUpperCase() };
      if (f.id) await apiClient.patch(`/hr/policies/${f.id}`, p); else await apiClient.post('/hr/policies', p);
      toast({ title: 'Saved' }); onSaved();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">{f.id ? 'Edit' : 'New'} policy</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 grid grid-cols-2 gap-3 overflow-y-auto">
          <Field label="Code"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="pc-code" /></Field>
          <Field label="Version"><Input value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="pc-title" /></Field></div>
          <Field label="Category"><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></Field>
          <Field label="Effective from"><Input type="date" value={f.effective_from || ''} onChange={(e) => setF({ ...f, effective_from: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Body (plain text or basic markdown)"><textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={10} className="w-full border border-slate-300 rounded p-2 text-sm" data-testid="pc-body" /></Field></div>
          <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.requires_ack} onChange={(e) => setF({ ...f, requires_ack: e.target.checked })} /> Requires acknowledgement</label>
          <div className="col-span-2 flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="pc-save">Save</Btn></div>
        </div>
      </div>
    </div>
  );
};

const PolicyViewModal = ({ policy, onClose }) => {
  const [acks, setAcks] = useState(null);
  useEffect(() => { apiClient.get(`/hr/policies/${policy.id}/acknowledgements`).then((r) => setAcks(r.data)).catch(() => setAcks({ data: [], total_users: 0, ack_count: 0 })); }, [policy.id]);
  const ackNow = async () => { try { await apiClient.post(`/hr/policies/${policy.id}/acknowledge`); toast({ title: 'Acknowledged' }); apiClient.get(`/hr/policies/${policy.id}/acknowledgements`).then((r) => setAcks(r.data)); } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); } };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl rounded-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div><div className="font-medium">{policy.title}</div><div className="text-xs text-[#7bbf95]">v{policy.version} · {policy.category}</div></div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 overflow-y-auto">
          <pre className="whitespace-pre-wrap text-sm text-[#0b1a2e]/80 font-sans">{policy.body}</pre>
          {acks && (
            <div className="mt-6 border-t pt-4">
              <div className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-2">Acknowledgements · {acks.ack_count} / {acks.total_users}</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {acks.data.map((a) => <div key={a.id} className="p-2 rounded bg-emerald-50 border border-emerald-200"><CheckCircle2 className="w-3.5 h-3.5 inline text-emerald-700 mr-1" />{a.user_name} <span className="text-[#0b1a2e]/50 ml-1">({a.acknowledged_at.slice(0,10)})</span></div>)}
              </div>
            </div>
          )}
        </div>
        <div className="p-4 border-t bg-[#f7fbf8] flex justify-end gap-2">
          {policy.requires_ack && <Btn onClick={ackNow} data-testid="ack-btn"><CheckCircle2 className="w-3.5 h-3.5" /> I acknowledge</Btn>}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// OFFBOARDING
// ============================================================
export const OffboardingPage = () => {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => {
    const [a, u] = await Promise.all([apiClient.get('/hr/offboarding'), apiClient.get('/users/directory')]);
    setItems(a.data.data || []); setUsers(u.data.data || []);
    if (selected) { const fresh = (a.data.data || []).find((x) => x.id === selected.id); if (fresh) setSelected(fresh); }
  }, [selected]);
  useEffect(() => { load(); }, []); // eslint-disable-line
  const sign = async (step) => {
    const note = window.prompt(`Sign-off note for ${step.label} (optional):`, '');
    try { await apiClient.post(`/hr/offboarding/${selected.id}/clearance/sign`, { step_code: step.code, note }); toast({ title: 'Signed off' }); load(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const complete = async () => { if (window.confirm('Complete offboarding & deactivate user?')) { try { await apiClient.post(`/hr/offboarding/${selected.id}/complete`); toast({ title: 'Completed' }); setSelected(null); load(); } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); } } };
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · Sprint 4" title="Offboarding" sub="Resignation → clearance → final settlement → experience letter → deactivation." />
        <div className="flex gap-2 mb-6"><Btn onClick={() => setShowNew(true)} data-testid="new-offboard-btn"><Plus className="w-3.5 h-3.5" /> Start offboarding</Btn></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-5">
          <div className="p-3 border-b border-black/5 text-xs text-[#0b1a2e]/60">{items.length} active / recent</div>
          <div className="max-h-[65vh] overflow-y-auto">
            {items.map((o) => (
              <button key={o.id} onClick={() => setSelected(o)} data-testid={`ob-row-${o.id}`}
                className={`w-full text-left p-3 border-b border-black/5 hover:bg-[#f7fbf8] ${selected?.id === o.id ? 'bg-[#f7fbf8]' : ''}`}>
                <div className="text-sm font-medium">{o.user_name}</div>
                <div className="text-[11px] text-[#0b1a2e]/50">LWD: {o.last_working_date} · <Badge color={o.status === 'completed' ? 'bg-slate-200 text-slate-800' : o.status === 'pending' ? 'bg-amber-100 text-amber-800' : o.status === 'ready_for_ff' ? 'bg-sky-100 text-sky-800' : o.status === 'ff_computed' ? 'bg-emerald-100 text-emerald-800' : 'bg-indigo-100 text-indigo-800'}>{o.status}</Badge></div>
              </button>
            ))}
            {items.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No offboardings.</div>}
          </div>
        </Card>
        <div className="lg:col-span-7">
          {!selected ? <Card className="p-16 text-center text-sm text-[#0b1a2e]/50">Select an offboarding to manage.</Card> : (
            <div className="space-y-3">
              <Card className="p-4">
                <div className="text-sm font-semibold flex items-center gap-2"><LogOut className="w-4 h-4 text-[#0f5c3d]" /> {selected.user_name}</div>
                <div className="text-xs text-[#0b1a2e]/60 mt-1">{selected.user_email}</div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                  <div>Resign date: <b>{selected.resignation_date}</b></div>
                  <div>Last working day: <b>{selected.last_working_date}</b></div>
                  <div>Status: <Badge color="bg-emerald-100 text-emerald-800">{selected.status}</Badge></div>
                </div>
                {selected.reason && <div className="mt-2 text-xs text-[#0b1a2e]/60">Reason: {selected.reason}</div>}
              </Card>
              <Card>
                <div className="p-3 border-b text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60">Clearance checklist</div>
                <div className="p-3 space-y-2">
                  {(selected.clearance || []).map((s) => (
                    <div key={s.code} className="flex items-center gap-2 border border-black/5 rounded p-2" data-testid={`clr-${s.code}`}>
                      <CheckCircle2 className={`w-4 h-4 ${s.status === 'signed_off' ? 'text-emerald-600' : 'text-slate-300'}`} />
                      <div className="flex-1">
                        <div className="text-sm">{s.label}</div>
                        {s.note && <div className="text-[11px] text-[#0b1a2e]/50">{s.note}</div>}
                      </div>
                      {s.status !== 'signed_off' && <Btn variant="ghost" onClick={() => sign(s)} data-testid={`sign-${s.code}`}>Sign off</Btn>}
                    </div>
                  ))}
                </div>
              </Card>
              <FFPanel offboarding={selected} onSaved={load} />
              <Card className="p-4 flex flex-wrap gap-2 justify-end">
                <a href={`${API}/api/hr/offboarding/${selected.id}/experience-letter.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#0f5c3d]/20 text-xs font-semibold text-[#0f5c3d]"><Download className="w-3.5 h-3.5" /> Experience letter</a>
                {selected.status !== 'completed' && <Btn onClick={complete} data-testid="ob-complete-btn"><LogOut className="w-3.5 h-3.5" /> Complete & deactivate</Btn>}
              </Card>
            </div>
          )}
        </div>
      </div>
      {showNew && <NewOffboardModal users={users} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
};

const FFPanel = ({ offboarding, onSaved }) => {
  const ff = offboarding.final_settlement || {};
  const [f, setF] = useState({ salary_days: ff.salary_days || 0, unused_leave_days: ff.unused_leave_days || 0, gratuity: ff.gratuity || 0, other_earnings: ff.other_earnings || 0, loan_deduction: ff.loan_deduction || 0, other_deductions: ff.other_deductions || 0, currency: ff.currency || 'USD', notes: ff.notes || '' });
  const compute = async () => {
    try { const p = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, typeof v === 'string' && k !== 'currency' && k !== 'notes' ? parseFloat(v || 0) : v]));
      await apiClient.post(`/hr/offboarding/${offboarding.id}/final-settlement`, p); toast({ title: 'F&F computed' }); onSaved(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <Card>
      <div className="p-3 border-b text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60">Full & final settlement</div>
      <div className="p-3 grid grid-cols-2 gap-3">
        {['salary_days', 'unused_leave_days', 'gratuity', 'other_earnings', 'loan_deduction', 'other_deductions'].map((k) => (
          <Field key={k} label={k.replace(/_/g, ' ')}><Input type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`ff-${k}`} /></Field>
        ))}
        <Field label="Currency"><Input value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })} /></Field>
        <div><Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field></div>
        {ff.net_payable != null && (
          <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded p-2 text-sm">
            Gross <b>{ff.gross}</b> · Deductions <b>{ff.deductions}</b> · <span className="text-emerald-700 font-bold">Net payable {ff.net_payable} {ff.currency}</span>
          </div>
        )}
        <div className="col-span-2 flex justify-end"><Btn onClick={compute} data-testid="ff-compute-btn">Compute F&F</Btn></div>
      </div>
    </Card>
  );
};

const NewOffboardModal = ({ users, onClose, onSaved }) => {
  const [f, setF] = useState({ user_id: '', resignation_date: new Date().toISOString().slice(0, 10), last_working_date: '', reason: '', notes: '' });
  const save = async () => {
    try { await apiClient.post('/hr/offboarding/resign', f); toast({ title: 'Offboarding started' }); onSaved(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">Start offboarding</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2"><Field label="Employee"><Select value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="ob-user"><option value="">Select</option>{users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}</Select></Field></div>
          <Field label="Resignation date"><Input type="date" value={f.resignation_date} onChange={(e) => setF({ ...f, resignation_date: e.target.value })} data-testid="ob-resign" /></Field>
          <Field label="Last working date"><Input type="date" value={f.last_working_date} onChange={(e) => setF({ ...f, last_working_date: e.target.value })} data-testid="ob-lwd" /></Field>
          <div className="col-span-2"><Field label="Reason"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field></div>
          <div className="col-span-2"><Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field></div>
          <div className="col-span-2 flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="ob-save">Start</Btn></div>
        </div>
      </div>
    </div>
  );
};
